import { useState, useMemo, useCallback } from "react";
import {
  Calendar as BigCalendar,
  dateFnsLocalizer,
  Views,
  type View,
  type SlotInfo,
} from "react-big-calendar";
import {
  it
} from "date-fns/locale";
import {
  format,
  parse,
  startOfWeek,
  getDay,
  addMonths,
  subMonths,
} from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
} from "lucide-react";
import { Button } from "../ui/Button";
import { cn } from "../../lib/utils";
import type { PackageItem, PackageItemType } from "../../types/database";
import "react-big-calendar/lib/css/react-big-calendar.css";

// Setup react-big-calendar localizer
const locales = {
  it: it,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: (date: Date) => startOfWeek(date, { locale: it }),
  getDay,
  locales,
});

// Calendar event type
interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: PackageItem;
  itemType: PackageItemType;
}

const itemTypeConfig: Record<
  PackageItemType,
  { label: string; color: string; bgColor: string }
> = {
  event: {
    label: "Evento",
    color: "#284899",
    bgColor: "rgba(40, 72, 153, 0.15)",
  },
  hotel: {
    label: "Hotel",
    color: "#3b82f6",
    bgColor: "rgba(59, 130, 246, 0.15)",
  },
  activity: {
    label: "Attività",
    color: "#10b981",
    bgColor: "rgba(16, 185, 129, 0.15)",
  },
  attraction: {
    label: "Attrazione",
    color: "#8b5cf6",
    bgColor: "rgba(139, 92, 246, 0.15)",
  },
  transport: {
    label: "Trasporto",
    color: "#6366f1",
    bgColor: "rgba(99, 102, 241, 0.15)",
  },
  other: {
    label: "Altro",
    color: "#64748b",
    bgColor: "rgba(100, 116, 139, 0.15)",
  },
};

interface PackageCalendarProps {
  items: PackageItem[];
  selectedItemId: string | null;
  onSelectItem: (itemId: string | null) => void;
  initialDate?: string | null;
}

export function PackageCalendar({
  items,
  selectedItemId,
  onSelectItem,
  initialDate,
}: PackageCalendarProps) {
  const [view, setView] = useState<View>(Views.MONTH);
  const [currentDate, setCurrentDate] = useState(() => {
    // Use package start_date if provided
    if (initialDate) return new Date(initialDate);
    // Otherwise use earliest item date
    const dated = items.filter(i => i.scheduled_date).sort((a, b) => a.scheduled_date!.localeCompare(b.scheduled_date!));
    if (dated.length > 0) return new Date(dated[0].scheduled_date!);
    return new Date();
  });

  // Convert package items to calendar events
  const events: CalendarEvent[] = useMemo(() => {
    return items
      .filter((item) => item.scheduled_date)
      .map((item) => {
        const startDate = new Date(item.scheduled_date!);
        
        // Set time if available
        if (item.scheduled_time) {
          const [hours, minutes] = item.scheduled_time.split(":");
          startDate.setHours(parseInt(hours), parseInt(minutes));
        }

        // Calculate end date
        let endDate: Date;
        if (item.duration_minutes) {
          endDate = new Date(startDate.getTime() + item.duration_minutes * 60000);
        } else {
          // Default 1 hour duration
          endDate = new Date(startDate.getTime() + 60 * 60000);
        }

        return {
          id: item.id,
          title: item.name,
          start: startDate,
          end: endDate,
          resource: item,
          itemType: item.item_type,
        };
      });
  }, [items]);

  // Handle event selection
  const handleSelectEvent = useCallback(
    (event: CalendarEvent) => {
      onSelectItem(event.id);
    },
    [onSelectItem]
  );

  // Handle slot selection (clicking on empty calendar space)
  const handleSelectSlot = useCallback(
    (_slotInfo: SlotInfo) => {
      onSelectItem(null);
    },
    [onSelectItem]
  );

  // Custom event style getter
  const eventStyleGetter = useCallback(
    (event: CalendarEvent) => {
      const config = itemTypeConfig[event.itemType];
      const isSelected = event.id === selectedItemId;

      return {
        style: {
          backgroundColor: config.bgColor,
          color: config.color,
          border: `2px solid ${config.color}`,
          borderRadius: "6px",
          padding: "2px 6px",
          fontSize: "0.875rem",
          fontWeight: isSelected ? "600" : "500",
          boxShadow: isSelected
            ? `0 0 0 3px ${config.color}40`
            : "0 1px 2px rgba(0,0,0,0.05)",
          outline: "none",
          cursor: "pointer",
          transition: "all 0.2s ease",
        },
      };
    },
    [selectedItemId]
  );

  // Navigation handlers
  const handleNavigate = useCallback((action: "PREV" | "NEXT" | "TODAY") => {
    setCurrentDate((prev) => {
      if (action === "TODAY") return new Date();
      
      const amount = action === "NEXT" ? 1 : -1;
      
      switch (view) {
        case Views.DAY:
        case Views.WEEK:
          return new Date(prev.setDate(prev.getDate() + amount * (view === Views.WEEK ? 7 : 1)));
        case Views.MONTH:
          return action === "NEXT" ? addMonths(prev, 1) : subMonths(prev, 1);
        default:
          return prev;
      }
    });
  }, [view]);

  // View change handler
  const handleViewChange = useCallback((newView: View) => {
    setView(newView);
  }, []);

  // Format toolbar label
  const getToolbarLabel = () => {
    switch (view) {
      case Views.DAY:
        return format(currentDate, "EEEE, d MMMM yyyy", { locale: it });
      case Views.WEEK:
        return format(currentDate, "MMMM yyyy", { locale: it });
      case Views.MONTH:
        return format(currentDate, "MMMM yyyy", { locale: it });
      default:
        return format(currentDate, "MMMM yyyy", { locale: it });
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Custom Toolbar */}
      <div className="flex items-center justify-between p-4 border-b border-border bg-card/50">
        <div className="flex items-center gap-2">
          <CalendarIcon className="h-5 w-5 text-[#284899]" />
          <h3 className="text-lg font-semibold capitalize">{getToolbarLabel()}</h3>
        </div>

        <div className="flex items-center gap-2">
          {/* Navigation */}
          <div className="flex items-center gap-1 mr-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleNavigate("PREV")}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={() => handleNavigate("TODAY")}
            >
              Oggi
            </Button>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleNavigate("NEXT")}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          {/* View Switcher */}
          <div className="flex items-center gap-1 p-1 bg-secondary/50 rounded-lg">
            {[
              { view: Views.DAY, label: "Giorno" },
              { view: Views.WEEK, label: "Settimana" },
              { view: Views.MONTH, label: "Mese" },
            ].map(({ view: v, label }) => (
              <Button
                key={v}
                variant={view === v ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "h-7 text-sm",
                  view === v && "bg-[#284899] text-white hover:bg-[#1e3570]"
                )}
                onClick={() => handleViewChange(v)}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="flex-1 p-4 overflow-hidden">
        <style>{`
          .rbc-calendar {
            font-family: inherit;
            height: 100%;
          }
          
          .rbc-header {
            padding: 12px 4px;
            font-weight: 600;
            font-size: 0.875rem;
            color: hsl(var(--foreground));
            border-bottom: 2px solid hsl(var(--border));
            background: hsl(var(--secondary) / 0.3);
          }
          
          .rbc-today {
            background-color: rgba(40, 72, 153, 0.05);
          }
          
          .rbc-off-range-bg {
            background-color: hsl(var(--muted) / 0.3);
          }
          
          .rbc-date-cell {
            padding: 6px;
            text-align: right;
          }
          
          .rbc-now .rbc-button-link {
            color: #284899;
            font-weight: 700;
          }
          
          .rbc-event {
            padding: 2px 5px;
            border-radius: 4px;
            cursor: pointer;
          }
          
          .rbc-event:focus {
            outline: none;
          }
          
          .rbc-event-label {
            font-size: 0.75rem;
          }
          
          .rbc-event-content {
            font-size: 0.875rem;
          }
          
          .rbc-month-view {
            border: 1px solid hsl(var(--border));
            border-radius: 8px;
            overflow: hidden;
            background: hsl(var(--card));
          }
          
          .rbc-time-view {
            border: 1px solid hsl(var(--border));
            border-radius: 8px;
            overflow: hidden;
            background: hsl(var(--card));
          }
          
          .rbc-time-header {
            border-bottom: 2px solid hsl(var(--border));
          }
          
          .rbc-time-content {
            border-top: 1px solid hsl(var(--border));
          }
          
          .rbc-day-slot .rbc-time-slot {
            border-top: 1px solid hsl(var(--border) / 0.3);
          }
          
          .rbc-current-time-indicator {
            background-color: #284899;
            height: 2px;
          }
          
          .rbc-timeslot-group {
            min-height: 60px;
            border-left: 1px solid hsl(var(--border));
          }
          
          .rbc-day-slot .rbc-event {
            border: 2px solid;
            border-left-width: 4px;
          }
          
          .rbc-slot-selection {
            background-color: rgba(40, 72, 153, 0.1);
            border: 2px dashed #284899;
          }
          
          .rbc-toolbar {
            display: none;
          }

          .rbc-time-header-content {
            border-left: 1px solid hsl(var(--border));
          }

          .rbc-time-content > * + * > * {
            border-left: 1px solid hsl(var(--border));
          }

          .rbc-agenda-view {
            background: hsl(var(--card));
            border: 1px solid hsl(var(--border));
            border-radius: 8px;
          }

          .rbc-agenda-view table.rbc-agenda-table {
            border-spacing: 0;
          }

          .rbc-agenda-view table.rbc-agenda-table tbody > tr > td {
            padding: 8px 12px;
            border-top: 1px solid hsl(var(--border));
          }

          .rbc-agenda-view table.rbc-agenda-table thead > tr > th {
            padding: 12px;
            border-bottom: 2px solid hsl(var(--border));
            background: hsl(var(--secondary) / 0.3);
            font-weight: 600;
          }
        `}</style>
        
        <BigCalendar
          localizer={localizer}
          events={events}
          view={view}
          onView={handleViewChange}
          date={currentDate}
          onNavigate={(date) => setCurrentDate(date)}
          onSelectEvent={handleSelectEvent}
          onSelectSlot={handleSelectSlot}
          eventPropGetter={eventStyleGetter}
          selectable
          popup
          culture="it"
          messages={{
            next: "Avanti",
            previous: "Indietro",
            today: "Oggi",
            month: "Mese",
            week: "Settimana",
            day: "Giorno",
            agenda: "Agenda",
            date: "Data",
            time: "Ora",
            event: "Evento",
            noEventsInRange: "Nessun evento in questo periodo",
            showMore: (total: number) => `+ Altri ${total}`,
          }}
          formats={{
            dayFormat: (date: Date) =>
              format(date, "EEE d/MM", { locale: it }),
            weekdayFormat: (date: Date) =>
              format(date, "EEEE", { locale: it }),
            timeGutterFormat: (date: Date) =>
              format(date, "HH:mm", { locale: it }),
            eventTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
              `${format(start, "HH:mm", { locale: it })} - ${format(end, "HH:mm", { locale: it })}`,
            agendaTimeRangeFormat: ({ start, end }: { start: Date; end: Date }) =>
              `${format(start, "HH:mm", { locale: it })} - ${format(end, "HH:mm", { locale: it })}`,
            agendaDateFormat: (date: Date) =>
              format(date, "dd/MM/yyyy", { locale: it }),
          }}
          min={new Date(2024, 0, 1, 7, 0, 0)}
          max={new Date(2024, 0, 1, 23, 0, 0)}
          step={30}
          timeslots={2}
        />
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-border bg-card/50">
        <div className="flex items-center gap-4 flex-wrap">
          <span className="text-sm font-medium text-muted-foreground">
            Legenda:
          </span>
          {Object.entries(itemTypeConfig).map(([key, config]) => (
            <div key={key} className="flex items-center gap-2">
              <div
                className="w-3 h-3 rounded border-2"
                style={{
                  backgroundColor: config.bgColor,
                  borderColor: config.color,
                }}
              />
              <span className="text-sm text-muted-foreground">
                {config.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

